/**
 * How a stored value reads to a person.
 *
 * Shared by the field flags and the save dialog, which have to agree: a value named one way beside
 * the control and another way in the list of what is about to be saved would read as two changes.
 */

import { t } from '~i18n';
import { Field, FieldValue, isOptionObject } from '../schema';

// A secret is worth reporting as changed, never worth printing. The control itself renders as a
// password input, so the value only ever escapes through these annotations.
export const SECRET_MASK = '••••••';
const isSecret = (field: Field): boolean => field.type === 'PasswordField';

// A drop-down stores an option's value and shows its label, so printing the value would name
// something the user never sees. Bare options (the timezone list) are their own label.
const optionLabel = (field: Field, value: FieldValue): string | undefined => {
  const option = field.options?.find((o) => isOptionObject(o) && o.value === value);
  return option && isOptionObject(option) && option.label_key ? t(option.label_key) : undefined;
};

export type Print = (value: FieldValue) => string;

// A list or an object has no one-line form: `String(value)` yields `[object Object]` or a bare
// comma-joined list, neither of which a reader can act on. `valuePrintable` keeps these off screen
// by field type; this is the backstop for a type that starts carrying one without saying so.
const isScalar = (value: FieldValue): boolean => value === null || value === undefined || typeof value !== 'object';

export const printer =
  (field: Field): Print =>
  (value) => {
    // Nothing there to print -- a layer holding no value, a key a device never had. No mask should
    // turn that into one.
    if (value === undefined || value === null) return '–';
    if (isSecret(field)) return SECRET_MASK;
    if (!isScalar(value)) return '–';
    return optionLabel(field, value) ?? String(value);
  };

// Field types whose value is named but never shown, because it has no readable one-line form.
// A control that holds a list has to say how it reads before it can be printed -- until it does,
// keeping it off screen is better than naming it wrongly.
const UNPRINTABLE_TYPES: ReadonlySet<Field['type']> = new Set(['Polygon']);

export const valuePrintable = (field: Field): boolean => !UNPRINTABLE_TYPES.has(field.type);
